import React, { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import useCustomReplies from '../../../hooks/useCustomReplies';

const AutoReplies = () => {
  const [activeTab, setActiveTab] = useState('custom');
  const [filter, setFilter] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [listName, setListName] = useState('Menu');
  const [editingListName, setEditingListName] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [customInputs, setCustomInputs] = useState([
    {
      input: 'Shared Inbox',
      response: "You can use Interakt's Shared Team Inbox to manage",
      type: 'Custom Message',
    },
    {
      input: 'WhatsApp Campaigns',
      response: 'WhatsApp Notification Campaigns are a great way to engage users',
      type: 'Product Message',
    },
    {
      input: 'Order Return Policy',
      response: 'You can register a return request only within the return period.',
      type: 'Custom Message',
    },
    {
      input: 'Order Delivery Time',
      response: 'We generally dispatch your order within 2 working days.',
      type: 'Custom Message',
    },
    {
      input: 'WhatsApp Automation',
      response: 'We know that your customers are reaching out to you frequently.',
      type: 'Custom Message',
    },
    {
      input: 'WhatsApp Commerce',
      response: 'Help your customers discover your product catalog easily.',
      type: 'Product Message',
    },
    {
      input: 'View Products',
      response: 'Click below to browse through our trending collections.',
      type: 'Product Message',
    },
  ]);
  const [newInputs, setNewInputs] = useState(['']);
  const [newResponse, setNewResponse] = useState('');
  const [newResponseType, setNewResponseType] = useState('Custom Message');

  const { createCustomReply, isLoading, error } = useCustomReplies();

  const listItems = [
    { input: 'Our Returns Policy', text: 'Our Returns Policy' },
    { input: 'Usual Delivery time', text: 'Usual Delivery time' },
    { input: 'About us', text: 'About us' },
    { input: 'View Products', text: 'View Products' },
  ];

  const filterOptions = ['is', 'is not', 'contains', 'does not contain'];

  const handleAddVariation = () => {
    if (newInputs.length < 5) {
      setNewInputs([...newInputs, '']);
    }
  };

  const handleInputChange = (index, value) => {
    const updatedInputs = [...newInputs];
    updatedInputs[index] = value;
    setNewInputs(updatedInputs);
  };

  const handleRemoveVariation = (index) => {
    const updatedInputs = newInputs.filter((_, i) => i !== index);
    setNewInputs(updatedInputs);
  };

  const handleAddCustomReply = async () => {
    if (!newInputs[0] || !newResponse) {
      alert('Please provide both input and response');
      return;
    }

    const replyData = {
      input: newInputs[0],
      variations: newInputs.slice(1).filter((v) => v.trim() !== ''),
      response: newResponse,
      type: newResponseType,
    };

    const result = await createCustomReply(replyData);

    if (result.success) {
      setCustomInputs([...customInputs, replyData]);
      setNewInputs(['']);
      setNewResponse('');
      setNewResponseType('Custom Message');
      setShowPopup(false);
      alert('Custom reply saved successfully!');
    } else {
      alert(result.error || 'Failed to save custom reply');
    }
  };

  return (
    <div className="p-4">
      <h2 className="fs-4 fw-bold mb-3">Automate conversations with users</h2>
      <p className="text-muted mb-4">
        Create Custom Auto Replies and Interactive List Messages for Frequently Asked Questions or otherwise to automate your conversations.
      </p>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'custom' ? 'active' : ''}`} onClick={() => setActiveTab('custom')}>
            Custom Auto Replies
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
            Interactive List Message
          </button>
        </li>
      </ul>

      {activeTab === 'custom' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Custom Auto Replies</h5>
            <button className="btn btn-success" onClick={() => setShowPopup(true)} disabled={isLoading}>
              + Add new Custom Reply
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="position-relative mb-3 w-auto">
            <div className="bg-danger text-white px-3 py-2 rounded" style={{ cursor: 'pointer' }} onClick={() => setFilterOpen(!filterOpen)}>
              Inputs not applied
            </div>
            {filterOpen && (
              <div className="position-absolute bg-white border rounded shadow p-2 mt-1" style={{ zIndex: 10, width: 220 }}>
                {filterOptions.map((option) => (
                  <div className="form-check" key={option}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="filter"
                      value={option}
                      checked={filter === option}
                      onChange={() => setFilter(option)}
                    />
                    <label className="form-check-label ms-2">{option}</label>
                  </div>
                ))}
                <div className="text-center mt-2">
                  <button className="btn btn-sm btn-light" onClick={() => setFilterOpen(false)}>Done</button>
                </div>
              </div>
            )}
          </div>

          {showPopup && (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Setup Custom Auto Reply</h5>
                    <button type="button" className="btn-close" onClick={() => setShowPopup(false)}></button>
                  </div>
                  <div
                    className="modal-body"
                    style={{
                      maxHeight: '60vh',
                      overflowY: 'auto',
                    }}
                  >
                    <div className="mb-3">
                      <label className="form-label fw-bold">Customer Input</label>
                      <p className="text-muted small">
                        Add the customer input for which you want the Custom Auto Reply to be triggered. As a user, you can enter the input and up to 5 variations of the same. In case the customer types in either of them, the Auto response gets triggered. Click <a href="#!">here</a> to see an example of how this works.
                      </p>
                      <p className="text-muted small">
                        While the Auto replies are triggered both for Customer Input and its variations, it is the input here that gets shown when the Interactive List Messages are set up.
                      </p>
                      {newInputs.map((input, index) => (
                        <div key={index} className="mb-2">
                          <div className="d-flex align-items-center">
                            <input
                              type="text"
                              className="form-control"
                              value={input}
                              onChange={(e) => handleInputChange(index, e.target.value)}
                              placeholder={index === 0 ? 'Input' : 'Variation'}
                              maxLength={70}
                              disabled={isLoading}
                            />
                            {index > 0 && (
                              <button
                                className="btn btn-sm btn-outline-danger ms-2"
                                onClick={() => handleRemoveVariation(index)}
                                disabled={isLoading}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                          <small className="text-muted">{input.length}/70</small>
                        </div>
                      ))}
                      {newInputs.length < 5 && (
                        <button className="btn btn-link p-0" onClick={handleAddVariation} disabled={isLoading}>
                          + Add another variation
                        </button>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Custom Auto Reply</label>
                      <p className="text-muted small">
                        Configure the type of Auto reply you want to be triggered when a customer sends the user input or one of its variations in the chat.
                      </p>
                      <div className="mb-3">
                        <label className="form-label">Select response type</label>
                        <div className="d-flex">
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="responseType"
                              value="Custom Message"
                              checked={newResponseType === 'Custom Message'}
                              onChange={(e) => setNewResponseType(e.target.value)}
                              disabled={isLoading}
                            />
                            <label className="form-check-label">Custom Message</label>
                          </div>
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="responseType"
                              value="Multi-Product Message"
                              checked={newResponseType === 'Multi-Product Message'}
                              onChange={(e) => setNewResponseType(e.target.value)}
                              disabled={isLoading}
                            />
                            <label className="form-check-label">Multi-Product Message</label>
                          </div>
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="responseType"
                              value="Workflow"
                              checked={newResponseType === 'Workflow'}
                              onChange={(e) => setNewResponseType(e.target.value)}
                              disabled={isLoading}
                            />
                            <label className="form-check-label">Workflow</label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="responseType"
                              value="Product Collection List"
                              checked={newResponseType === 'Product Collection List'}
                              onChange={(e) => setNewResponseType(e.target.value)}
                              disabled={isLoading}
                            />
                            <label className="form-check-label">Product Collection List</label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Message body</label>
                        <textarea
                          className="form-control"
                          value={newResponse}
                          onChange={(e) => setNewResponse(e.target.value)}
                          placeholder="Enter your auto response message here"
                          rows={4}
                          maxLength={500}
                          style={{ resize: 'vertical' }}
                          disabled={isLoading}
                        />
                        <small className="text-muted">{newResponse.length}/500</small>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowPopup(false)} disabled={isLoading}>
                      Close
                    </button>
                    <button type="button" className="btn btn-success" onClick={handleAddCustomReply} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Input</th>
                  <th>Auto Response Preview</th>
                  <th>Response Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customInputs.map((item, index) => (
                  <tr key={index}>
                    <td>{item.input}</td>
                    <td>{item.response}</td>
                    <td>{item.type}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-2">
                        <Pencil size={16} />
                      </button>
                      <button className="btn btn-sm btn-outline-danger">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'list' && (
        <>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <p className="text-muted">
                Interactive List Messages are used to trigger Custom Auto Replies in form of a List Message. This gives customers the flexibility
                to enter their query, converse with your business via free text or List Messages thereby improving the overall customer experience.
                You can add up to 10 items and re-order them as well. <a href="#!">Click here</a> to read more.
              </p>
            </div>
            <button className="btn btn-success">+ Include new Reply</button>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold me-2">Interactive list Name</label>
            {editingListName ? (
              <input
                className="form-control d-inline w-auto"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                onBlur={() => setEditingListName(false)}
                autoFocus
              />
            ) : (
              <span
                className="border rounded px-3 py-1 bg-light"
                onClick={() => setEditingListName(true)}
                style={{ cursor: 'pointer' }}
              >
                {listName} <Pencil size={14} className="ms-1" />
              </span>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Input</th>
                  <th>List Item text</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((item, index) => (
                  <tr key={index}>
                    <td>⋮⋮ {item.input}</td>
                    <td>{item.text}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-2">
                        <Pencil size={16} />
                      </button>
                      <button className="btn btn-sm btn-outline-danger">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AutoReplies;